import React, { FunctionComponent, Suspense, useState, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { ActionId } from 'admin/actions/Actions';
import { getDesktopAggregatedAlerts, updateRemindMeLater } from 'admin/notifications/Notifications';
import { DesktopAlert } from 'admin/notifications/Notifications.types';
import { useProfile, isAuthorized } from 'auth/Auth';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import {
    getRegisteredEventTypes,
    getDesktopAlertLoader,
} from 'common/notification-tray/desktop-alert-registry/DesktopAlertRegistry';
import { DesktopNotificationAlert } from 'common/notification-tray/desktop-notification-alert/DesktopNotificationAlert';
import {
    USE_MOCK_DATA,
    getMockDesktopAlerts,
} from 'common/notification-tray/desktop-notification-alert/desktopNotificationMockData';
import { useAdaptivePolling } from 'common/notification-tray/desktop-notification-alert/useAdaptivePolling';

interface Props {
    hidden?: boolean;
}

export const DesktopAlertManager: FunctionComponent<Props> = ({ hidden = false }) => {
    const [{ profile }] = useProfile();
    const { t } = useTranslation('admin/notifications');

    const [desktopAlerts, setDesktopAlerts] = useState<DesktopAlert[]>([]);
    const [activeAlert, setActiveAlert] = useState<DesktopAlert | null>(null);
    const lazyComponentCache = useRef<Map<string, React.LazyExoticComponent<any>>>(new Map());
    const [, dispatchMessage] = useMessage();

    const removeAlert = useCallback((alertId: string): void => {
        setDesktopAlerts((prev) => prev.filter((a) => a.id !== alertId));
    }, []);

    const handleRemindMeLaterAlert = useCallback(
        async (alert: DesktopAlert): Promise<void> => {
            removeAlert(alert.id);

            try {
                if (!USE_MOCK_DATA && profile) {
                    if (alert.notificationHistoryIDs?.length > 0) {
                        await updateRemindMeLater(profile?.tenant?.id, alert.notificationHistoryIDs);
                    }
                }
                dispatchMessage({
                    type: MessageActionType.ShowSuccess,
                    text: t('REMIND_ME_LATER_SUCCESS'),
                });
            } catch {
                dispatchMessage({
                    type: MessageActionType.ShowError,
                    text: t('REMIND_ME_LATER_ERROR'),
                });
            }
        },
        [profile, removeAlert, dispatchMessage, t],
    );

    const handleAlertAction = useCallback((alert: DesktopAlert): void => {
        setActiveAlert(alert);
        setDesktopAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    }, []);

    const handleCloseActiveAlert = useCallback((): void => {
        setActiveAlert(null);
    }, []);

    // Polling
    const pollDesktopAlerts = useCallback(async (): Promise<boolean> => {
        try {
            if (!isAuthorized({ actions: [ActionId.PlatformNotifications.View] })) {
                return false;
            }

            let alerts: DesktopAlert[];

            if (USE_MOCK_DATA) {
                alerts = getMockDesktopAlerts();
            } else {
                if (!profile?.tenant?.id) {
                    return false;
                }
                alerts = await getDesktopAggregatedAlerts(profile.tenant.id, {
                    eventTypes: getRegisteredEventTypes(),
                });
            }

            if (alerts?.length > 0) {
                setDesktopAlerts(alerts);
                return true;
            }

            return false;
        } catch {
            return false;
        }
    }, [profile]);

    useAdaptivePolling(pollDesktopAlerts, {
        minInterval: 30000,
        maxInterval: 600000,
        backoffMultiplier: 1.5,
        enabled: !!profile && !profile.isAgencyUser,
    });

    const renderActiveAlertComponent = useCallback(
        (alert: DesktopAlert): JSX.Element | null => {
            const loader = getDesktopAlertLoader(alert.eventType);

            if (!loader) {
                return null;
            }

            let LazyComponent = lazyComponentCache.current.get(alert.eventType);

            if (!LazyComponent) {
                LazyComponent = React.lazy(loader);
                lazyComponentCache.current.set(alert.eventType, LazyComponent);
            }

            return (
                <Suspense fallback={null}>
                    <LazyComponent alert={alert} onClose={handleCloseActiveAlert} />
                </Suspense>
            );
        },
        [handleCloseActiveAlert],
    );

    return (
        <>
            {!hidden && (
                <DesktopNotificationAlert
                    alerts={desktopAlerts}
                    onDismiss={removeAlert}
                    onRemindMeLater={handleRemindMeLaterAlert}
                    onAction={handleAlertAction}
                />
            )}

            {activeAlert && renderActiveAlertComponent(activeAlert)}
        </>
    );
};
